import { callAPI } from '../../shared/helpers';
import { clearEventSlice, fullClearEventSlice, fetchResultScope, toggleFullLeaderboard, toggleFavorite, syncFavoritesFromStorage } from './event_index';
import { fetchPreviewEvents, commitEvents } from './leaderboardV2_index';
import { loadFavorites, saveFavorites } from './helpers/local_storage_helper';
import { scrollToAggregate, aggregateIdForMember } from './helpers/scroll_helper';

export const SEARCH_MEMBERS_REQUEST = 'memberSearch/SEARCH_MEMBERS_REQUEST';
export const SEARCH_MEMBERS_SUCCESS = 'memberSearch/SEARCH_MEMBERS_SUCCESS';
export const SEARCH_MEMBERS_FAILURE = 'memberSearch/SEARCH_MEMBERS_FAILURE';
export const SEARCH_MEMBERS_CLEAR = 'memberSearch/SEARCH_MEMBERS_CLEAR';
export const SELECT_MEMBER_RESULT = 'memberSearch/SELECT_MEMBER_RESULT';
export const FETCH_MEMBER_AGGREGATES_REQUEST = 'memberSearch/FETCH_MEMBER_AGGREGATES_REQUEST';
export const FETCH_MEMBER_AGGREGATES_SUCCESS = 'memberSearch/FETCH_MEMBER_AGGREGATES_SUCCESS';
export const FETCH_MEMBER_AGGREGATES_FAILURE = 'memberSearch/FETCH_MEMBER_AGGREGATES_FAILURE';
export const CLEAR_MEMBER_AGGREGATES = 'memberSearch/CLEAR_MEMBER_AGGREGATES';

// Search spans the whole league — except on customized tournament results
// pages, whose round entries carry the hand-picked event ids the results
// must stay within.
const customizedEventIds = (state) =>
  (state.leaderboardV2.roundList || []).flatMap(round => round.event_ids || []);

const searchParams = (state, params = {}) => {
  const eventIds = customizedEventIds(state);
  return eventIds.length ? { ...params, event_ids: eventIds } : params;
};

const buildSearchUrl = (state) => state.leaderboardV2.memberSearchUrl;

const buildAggregatesUrl = (state, memberId) =>
  state.leaderboardV2.memberAggregatesUrl.replace(':member_id', memberId)

export const searchMembers = (query) => (dispatch, getState) => {
  const state = getState();
  const url = buildSearchUrl(state);

  dispatch({ type: SEARCH_MEMBERS_REQUEST });
  // Snapshot the generation so a response is ignored once the search has been
  // cleared (e.g. clicking outside mid-load) or superseded by a newer query.
  const requestId = getState().memberSearch.requestId;
  const isStale = () => getState().memberSearch.requestId !== requestId;

  return callAPI(url, 'GET', searchParams(state, { query: query }))
    .then((results) => {
      if (isStale()) return;

      dispatch({ type: SEARCH_MEMBERS_SUCCESS, payload: results });
    })
    .catch(() => {
      if (isStale()) return;
      dispatch({ type: SEARCH_MEMBERS_FAILURE });
    });
};

export const clearMemberSearch = () => ({ type: SEARCH_MEMBERS_CLEAR });

export const fetchMemberAggregates = (memberId) => (dispatch, getState) => {
  const state = getState();
  const url = buildAggregatesUrl(state, memberId);

  dispatch({ type: FETCH_MEMBER_AGGREGATES_REQUEST, payload: { memberId } });

  return callAPI(url, 'GET', searchParams(state))
    .then((aggregates) => dispatch({ type: FETCH_MEMBER_AGGREGATES_SUCCESS, payload: aggregates }))
    .catch(() => dispatch({ type: FETCH_MEMBER_AGGREGATES_FAILURE }));
};

export const clearMemberAggregates = () => ({ type: CLEAR_MEMBER_AGGREGATES });

// The loaded event slices are checked too: during an optimistic add the
// slice is ahead of the favoritesByEvent mirror (deferListMirror), and an
// unstar clicked in that window must still take the remove path.
const isMemberFavorite = (state, memberIdStr) =>
  Object.values(state.leaderboardV2.favoritesByEvent).some(
    memberIds => memberIds.includes(memberIdStr)
  ) ||
  Object.values(state.event).some(
    eventState => (eventState?.favorites || []).includes(memberIdStr)
  );

const loadedEventIdForParent = (event, loadedEventIds) =>
  [String(event.id), ...(event.snapshots || []).map(s => String(s.id))]
    .find(id => loadedEventIds.includes(id));

const fetchPlayedEventIds = (state, memberId) => {
  const { expandedMemberId, aggregates } = state.memberSearch;
  if (String(expandedMemberId) === String(memberId) && aggregates.length > 0) {
    return Promise.resolve(aggregates.map(agg => agg.event_id));
  }

  return callAPI(buildAggregatesUrl(state, memberId), 'GET', searchParams(state))
    .then(played => played.map(agg => agg.event_id))
    .catch(() => []);
};

const addMemberFavoriteForPlayedEvents = (memberId, playedEventIds) => (dispatch, getState) => {
  const memberIdStr = String(memberId);
  const state = getState();
  const loadedEventIds = Object.keys(state.event);
  const storedFavorites = loadFavorites();
  const listEventsById = new Map(
    Object.values(state.leaderboardV2.eventsList).flat().map(ev => [String(ev.id), ev])
  );

  // Played ids come parent-resolved from the aggregates API and cover every
  // league round; eventsList only holds the current round, so an id absent
  // from it (another round's event) still gets its bucket written directly.
  playedEventIds.forEach(playedId => {
    const parentId = String(playedId);
    const listEvent = listEventsById.get(parentId);
    const loadedEventId = listEvent && loadedEventIdForParent(listEvent, loadedEventIds);
    if (loadedEventId) {
      if (!(state.event[loadedEventId]?.favorites || []).includes(memberIdStr)) {
        // The list mirror is settled once, by the caller's final
        // syncFavoritesFromStorage - these dispatches run in a promise
        // callback (unbatched on React 16), so updating the mirror here
        // would flash the events-list split through intermediate states.
        dispatch(toggleFavorite(loadedEventId, memberId, { deferListMirror: true }));
      }
      return;
    }

    const currentList = storedFavorites[parentId] || [];
    if (!currentList.includes(memberIdStr)) {
      saveFavorites(parentId, [...currentList, memberIdStr]);
    }
  });
};

const removeMemberFavoriteEverywhere = (memberId) => (dispatch, getState) => {
  const memberIdStr = String(memberId);
  const state = getState();
  const loadedEventIds = Object.keys(state.event);
  const storedFavorites = loadFavorites();

  loadedEventIds
    .filter(eventId => (state.event[eventId]?.favorites || []).includes(memberIdStr))
    .forEach(eventId => dispatch(toggleFavorite(eventId, memberId)));

  // Buckets the dispatches above already rewrote through toggleFavorite's own
  // storage write - the stale copies read before them must not be written back.
  const loadedParentIds = new Set(
    Object.values(state.leaderboardV2.eventsList).flat()
      .filter(ev => loadedEventIdForParent(ev, loadedEventIds))
      .map(ev => String(ev.id))
  );

  // Every stored bucket holding the member, whatever round its event belongs
  // to - the current round's eventsList only ever covers a slice of them.
  Object.entries(storedFavorites).forEach(([parentId, list]) => {
    if (loadedParentIds.has(parentId)) return;
    if (!(list || []).includes(memberIdStr)) return;

    saveFavorites(parentId, list.filter(id => id !== memberIdStr));
  });
};

// Invalidates in-flight played-events fan-outs: a newer toggle for the same
// member bumps the generation, so an older response landing after a
// direction flip (star, quick unstar) cannot write its buckets back.
const favoriteToggleGenerations = new Map();

export const toggleMemberFavorite = (memberId, clickedEventId = null) => (dispatch, getState) => {
  const memberIdStr = String(memberId);

  const generation = (favoriteToggleGenerations.get(memberIdStr) ?? 0) + 1;
  favoriteToggleGenerations.set(memberIdStr, generation);

  const finalize = () => {
    // Sync favoritesByEvent from localStorage so "My Leaderboards" and the
    // search dropdown stars reflect the updated favorites even for events
    // that were never loaded into Redux state.
    dispatch(syncFavoritesFromStorage());
  };

  // Direction is decided once, up front: the optimistic toggle below flips
  // isMemberFavorite, so re-evaluating later would reverse the action.
  if (isMemberFavorite(getState(), memberIdStr)) {
    dispatch(removeMemberFavoriteEverywhere(memberId));
    finalize();
    return Promise.resolve();
  }

  // Optimistic phase (row stars only - the search star has no event
  // context): the clicked event is favorited synchronously so its star and
  // favorites table render before the network resolves which other events
  // the member played. The events-list mirror is deferred so the
  // "My Leaderboards"/"Leaderboards" headers regroup once, at finalize,
  // never through the one-event intermediate state. toggleFavorite is
  // persist-first: a failed storage write toasts and changes nothing, in
  // which case the fan-out must not run either - the unchanged slice is
  // the abort signal.
  if (clickedEventId && !(getState().event[clickedEventId]?.favorites || []).includes(memberIdStr)) {
    dispatch(toggleFavorite(clickedEventId, memberId, { deferListMirror: true }));
    if (!(getState().event[clickedEventId]?.favorites || []).includes(memberIdStr)) return Promise.resolve();
  }

  return fetchPlayedEventIds(getState(), memberId).then((playedEventIds) => {
    if (favoriteToggleGenerations.get(memberIdStr) !== generation) return;

    dispatch(addMemberFavoriteForPlayedEvents(memberId, playedEventIds));
    finalize();
  });
};

// Opens the event of the selected search result and scrolls to the member's
// row. Already-loaded events scroll right away; the rest scroll after
// fetchResultScope succeeds (the scroll target is passed along with the
// fetch, so an aborted or stale fetch can never scroll).
export const selectMemberResult = ({ eventId, aggregateId, memberId, roundId: eventRoundId }) => async (dispatch, getState) => {
  // Close the dropdown right away — the round switch below may need a fetch,
  // and the results must not linger over the board while it runs.
  dispatch({ type: SELECT_MEMBER_RESULT });

  let { eventsList, roundId, roundList } = getState().leaderboardV2;

  let eventEntry = Object.values(eventsList).flat().find(e => e.id === eventId);

  // The result may belong to another round (search spans the whole league):
  // switch to it first, exactly like picking it in the rounds modal.
  if (!eventEntry && eventRoundId && String(eventRoundId) !== String(roundId)) {
    const preview = await dispatch(fetchPreviewEvents(eventRoundId));
    if (preview?.status === 'ok') {
      dispatch(commitEvents(String(eventRoundId), preview.events));
      dispatch(fullClearEventSlice());
      ({ eventsList, roundId, roundList } = getState().leaderboardV2);
      eventEntry = Object.values(eventsList).flat().find(e => e.id === eventId);
    }
  }

  let resolvedEventId = eventId;
  if (eventEntry?.snapshots?.length > 0) {
    const currentRound = roundList.find(r => r.id === roundId);
    resolvedEventId = eventEntry.snapshots.find(s => s.index === currentRound?.index)?.id ?? eventId;
  }

  dispatch(clearEventSlice(resolvedEventId));

  const eventState = getState().event[resolvedEventId];
  if (eventState?.data?.scopes) {
    // The searched member may not be a favorite, so their row only exists
    // in the full-leaderboard table — expand it before scrolling.
    if (eventState.showFullLeaderboard === false) {
      dispatch(toggleFullLeaderboard(resolvedEventId));
    }
    scrollToAggregate(aggregateIdForMember(eventState.data, memberId) ?? aggregateId);
  } else {
    dispatch(fetchResultScope(resolvedEventId, {
      scrollToAggregateId: aggregateId,
      scrollToMemberId: memberId,
    }));
  }
};
